<app-page-layout
  pageTitle="API Groups"
  subtitle="Organize routes by business domain, backend system, platform or environment."
>
  <div page-actions class="flex items-center gap-2">
    <p-button
      label="Create API group"
      icon="pi pi-plus"
      severity="primary"
      (onClick)="openCreateGroup()"
      [loading]="isLoading()"
      outlined
    ></p-button>
  </div>

  <p-toast></p-toast>

  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading API groups..."
    loadingSubMessage="Please wait while we fetch your API groups"
    emptyIcon="pi-sitemap"
    emptyTitle="No API groups found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="true"
    emptyActionLabel="Create API Group"
    emptyActionIcon="pi-plus"
    [emptyActionOutlined]="true"
    [showClearFilters]="false"
    [errorMessage]="errorMessage()"
    (emptyAction)="openCreateGroup()"
    (retry)="retryLoad()"
  >
    <div class="space-y-6">
      <div class="grid gap-4 xl:grid-cols-3">
        @for (group of apiGroups; track group.id; let i = $index) {
          <div class="rounded-xl border border-surface-200 bg-surface-0 p-5 shadow-sm dark:border-surface-700 dark:bg-surface-900">
            <div class="mb-3 flex items-start justify-between gap-3">
              <h3 class="text-base font-semibold text-surface-800 dark:text-surface-100">{{ group.name }}</h3>
            </div>

            <p class="mb-3 text-sm text-surface-500 dark:text-surface-400">{{ group.members }}</p>

            <div class="mb-4 flex flex-wrap gap-2">
            </div>

            <div class="mb-3 flex min-w-0 items-center gap-1 overflow-hidden">
              <div class="flex min-w-0 shrink gap-1 overflow-hidden">
                @for (scope of getVisibleScopes(group.scope); track scope) {
                  <span class="inline-flex min-w-0 max-w-32 shrink-0 truncate rounded-full bg-primary-100 px-2.5 py-1 text-[10px] font-medium text-primary-700 dark:bg-primary-500/20 dark:text-primary-300">
                    {{ scope }}
                  </span>
                }
              </div>

              @if (getRemainingScopesCount(group.scope) > 0) {
                <span class="inline-flex shrink-0 cursor-help rounded-full bg-surface-100 px-2.5 py-1 text-[10px] font-medium text-surface-700 dark:bg-surface-500/20 dark:text-surface-300"
                      [pTooltip]="getRemainingScopesTooltip(group.scope)"
                      tooltipPosition="top"
                      tooltipStyleClass="scopes-tooltip">
                  +{{ getRemainingScopesCount(group.scope) }}
                </span>
              }

              @if (!group.scope || group.scope.length === 0) {
                <span class="text-xs text-surface-400 dark:text-surface-500">
                  No scopes assigned
                </span>
              }
            </div>

            <p class="mb-4 text-sm text-surface-600 dark:text-surface-300">
              <span class="font-semibold text-surface-700 dark:text-surface-200">Policy:</span> {{ group.policy }}
            </p>

            <div class="mb-4 grid grid-cols-3 gap-2 text-xs text-surface-500 dark:text-surface-400">
              <div class="flex items-center gap-1">
                <i class="pi pi-bolt"></i>
                <span>{{ group.rateLimit || 'N/A' }}</span>
              </div>
              <div class="flex items-center gap-1">
                <i class="pi pi-clock"></i>
                <span>{{ group.timeout || 'N/A' }}s</span>
              </div>
              <div class="flex items-center gap-1">
                <i class="pi pi-sitemap"></i>
                <span>{{ group.pathHandling || 'Preserve' }}</span>
              </div>
            </div>

            <div class="flex flex-wrap gap-2">
              <p-button
                label="Configure"
                severity="secondary"
                variant="outlined"
                size="small"
                (onClick)="configureGroup(group, i)"
                pTooltip="Configure group"
                tooltipPosition="top"
              ></p-button>
            </div>
          <!-- <p-button
            label="View Members"
            severity="secondary"
            variant="outlined"
            size="small"
            (onClick)="viewRoutes(group)"
            pTooltip="View API Members"
            tooltipPosition="top"
          ></p-button> -->
        </div>
        }
      </div>
    </div>
  </app-state-container>
</app-page-layout>

<!-- Create/Configure API Group Dialog -->
<p-dialog
  [(visible)]="showModal"
  [modal]="true"
  [dismissableMask]="true"
  [style]="{ width: '42rem' }"
  [breakpoints]="{ '960px': '90vw' }"
  [header]="editingIndex === null ? 'Create API Group' : 'Configure API Group'"
>
  <div class="space-y-4">
    <div class="grid gap-4 md:grid-cols-2">
      <!-- Group Name -->
      <div class="space-y-1.5">
        <label class="text-[10px] font-medium uppercase tracking-[0.08em] text-surface-500 dark:text-surface-400">Group name</label>
        <input
          pInputText
          type="text"
          class="w-full"
          [(ngModel)]="editForm.name"
          placeholder="e.g. Dealer Commerce APIs"
        />
      </div>

      <div class="space-y-1.5">
        <label class="text-[10px] font-medium uppercase tracking-[0.08em] text-surface-500 dark:text-surface-400">Policy baseline</label>
        <p-select
          [options]="policyOptions"
          [(ngModel)]="editForm.policyId"
          class="w-full"
          placeholder="Select policy baseline"
          (onChange)="onPolicyChange()"
          appendTo="body"
          [loading]="isLoadingPolicies"
        >
          <ng-template let-item>
            {{ item.label }}
          </ng-template>
        </p-select>
      </div>

      <div class="space-y-1.5 md:col-span-2">
        <label class="text-[10px] font-medium uppercase tracking-[0.08em] text-surface-500 dark:text-surface-400">Default allowed scopes</label>
        <div class="min-h-[42px] rounded-lg border border-surface-200 bg-surface-50 p-3 dark:border-surface-700 dark:bg-surface-800">
          <div class="flex flex-wrap gap-2">
            @for (scope of editForm.scope; track scope) {
              <span class="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-500/20 dark:text-primary-300">
                {{ scope }}
              </span>
            }
            @if (editForm.scope.length === 0) {
              <span class="text-sm text-surface-400 dark:text-surface-500">
                Choose a policy
              </span>
            }
          </div>
        </div>
      </div>

      <!-- Rate limit -->
      <div class="space-y-1.5">
        <label class="text-[10px] font-medium uppercase tracking-[0.08em] text-surface-500 dark:text-surface-400">Rate limit</label>
        <p-select
          [options]="rateLimitOptions"
          [(ngModel)]="editForm.rateLimit"
          class="w-full"
          placeholder="Select rate limit"
          appendTo="body"
        >
          <ng-template pTemplate="selectedItem" let-item>
            {{ item === 'Unlimited' ? item : item + ' requests / minute' }}
          </ng-template>
          <ng-template let-item>
            {{ item === 'Unlimited' ? item : item + ' requests / minute' }}
          </ng-template>
        </p-select>
      </div>

      <!-- Upstream Timeout -->
      <div class="space-y-1.5">
        <label class="text-[10px] font-medium uppercase tracking-[0.08em] text-surface-500 dark:text-surface-400">Upstream timeout</label>
        <p-select
          [options]="timeoutOptions"
          [(ngModel)]="editForm.timeout"
          class="w-full"
          placeholder="Select timeout"
          appendTo="body"
        >
          <ng-template pTemplate="selectedItem" let-item>
            {{ item }} seconds
          </ng-template>
          <ng-template let-item>
            {{ item }} seconds
          </ng-template>
        </p-select>
      </div>

      <!-- Path Handling -->
      <div class="space-y-1.5">
        <label class="text-[10px] font-medium uppercase tracking-[0.08em] text-surface-500 dark:text-surface-400">Path handling</label>
        <p-select
          [options]="pathHandlingOptions"
          [(ngModel)]="editForm.pathHandling"
          class="w-full"
          appendTo="body"
          placeholder="Select path handling"
        ></p-select>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-end gap-3">
      <p-button label="Cancel" severity="secondary" variant="outlined" (onClick)="closeModal()"></p-button>
      <p-button label="Save group" severity="primary" (onClick)="saveGroup()" [loading]="isLoading()"></p-button>
    </div>
  </ng-template>
</p-dialog>